/**
 * Which language the game speaks.
 *
 * Ukrainian is the original and stays the source of truth: it is the language
 * the game was designed in, the one the block names were chosen in, and the
 * one a missing translation falls back to.
 *
 * **English is a required field, not a lookup table.** Every block, item and
 * line carries its English alongside its Ukrainian, and the types say so - so
 * adding a block without translating it does not compile. A side table keyed
 * by name would have been less to write and would have let a half-translated
 * game ship, with the gap showing up as one Ukrainian word in an English
 * sentence, in front of whoever is playing.
 *
 * Tone and language are separate axes. Tone is how blunt the game is; language
 * is which words it uses. Both apply at once: there is a family English and an
 * adult English, exactly as there is a family Ukrainian and an adult one.
 */

export type Lang = "uk" | "en";

/** The one the game was written in. Anything unrecognised lands here. */
export const DEFAULT_LANG: Lang = "uk";

export function parseLang(raw: string | null): Lang {
  return raw === "en" ? "en" : DEFAULT_LANG;
}

/** What the switch on the home screen says, in each language. */
export const LANG_LABELS: Readonly<Record<Lang, string>> = {
  uk: "Українська",
  en: "English",
};

/**
 * A thing said in both languages.
 *
 * Used for everything that is not a block, an item or a line of dialogue -
 * button captions, headings, the words on the home screen.
 */
export interface Phrase {
  readonly uk: string;
  readonly en: string;
}

export function pick(phrase: Phrase, lang: Lang): string {
  return lang === "en" ? phrase.en : phrase.uk;
}

/**
 * Everything the interface itself says.
 *
 * Keyed rather than inlined so that a language switch is one lookup and a
 * missing translation is impossible: `Phrase` requires both halves.
 */
export const UI = {
  // ---- the home screen ---------------------------------------------------
  "home-paused": {
    uk: "ПАУЗА — світ зупинено. Клацни, щоб грати далі",
    en: "PAUSED — the world is stopped. Click to carry on",
  },
  "home-controls": { uk: "Керування", en: "Controls" },
  "home-settings": { uk: "Налаштування", en: "Settings" },
  "store-search": { uk: "Пошук за назвою", en: "Search by name" },
  "store-none": { uk: "Нічого не знайшлося", en: "Nothing found" },
  "home-saves": { uk: "Збереження", en: "Saved games" },
  "home-play": { uk: "click to play", en: "click to play" },
  /**
   * What the screen says when the pointer cannot be taken.
   *
   * On a phone there is no pointer to lock, so «Почати гру» grew a world and
   * then left the player looking at a pause screen with "click to play" on
   * it and nothing to click. Saying what is actually wrong is the least this
   * can do until there is a way to play with a thumb.
   */
  "home-no-pointer": {
    uk: "Пальцем: ліворуч — ходити, праворуч — дивитися, тап — копати, " +
      "притримати — поставити",
    en: "By finger: left to walk, right to look, tap to dig, hold to place",
  },
  "home-creative": { uk: "Режим творця", en: "Creative mode" },
  "home-creative-note": {
    uk: "у речах з'явиться комора: бери що завгодно й скільки завгодно",
    en: "a storeroom appears in your bag: take anything, as much as you like",
  },
  "home-keep": {
    uk: "Не губити речі після смерті",
    en: "Keep your things when you die",
  },
  "home-keep-note": {
    uk: "без галочки все випадає там, де ти загинув",
    en: "unticked, everything drops where you fell",
  },
  "home-hardcore": { uk: "Hardcore", en: "Hardcore" },
  "home-hardcore-note": {
    uk: "десять тихих хвилин, тоді орки йдуть на твій острів, їх удвічі " +
      "більше, і перший напад ще за п'ять — але при тобі буде чотири скрині спорядження",
    en: "ten quiet minutes, then the orcs march on your island, twice as " +
      "many of them, and the first attack five minutes after - but four " +
      "chests of gear stand beside you",
  },
  "home-adult": {
    uk: "дорослі жарти та міцне слівце",
    en: "grown-up jokes and strong language",
  },
  "home-adult-note": {
    uk: "без галочки гра лишається доброю й без лайки",
    en: "unticked, the game stays kind and clean",
  },
  /* «Сучасна війна»: the lobby, the clock and the table at the end. */
  "war-prepare": { uk: "до бою", en: "until the battle" },
  "war-prep-length": { uk: "часу на підготовку", en: "time to prepare" },
  "war-lobby": {
    uk: "Обери команду й чекай, поки почнеться бій",
    en: "Pick a team and wait for the match to start",
  },
  "war-need-two": {
    uk: "Щоб почати, потрібні дві команди з гравцями. Або створи гру «проти ботів»",
    en: "Two teams with people in them are needed to start. Or make the game against bots",
  },
  "war-need-side": {
    uk: "Приєднайся до команди людей, і можна починати проти ботів",
    en: "Join the side of people, and the game against the bots can start",
  },
  "war-lobby-owner": {
    uk: "Створи команди, дочекайся гравців і натисни «Почати»",
    en: "Make the teams, wait for people to join, then press Start",
  },
  "war-no-teams": {
    uk: "Команд ще немає",
    en: "No teams yet",
  },
  "war-join": { uk: "Доєднатись", en: "Join" },
  "war-won": { uk: "Перемогла команда", en: "Winning team" },
  "war-nobody": { uk: "Нічия: всі прапори впали", en: "A draw: every flag fell" },
  "war-time-up": { uk: "Час гри вийшов. Світ зникне за хвилину", en: "The game's time is up. The world goes in a minute" },
  "war-ends": { uk: "до кінця гри", en: "until the game ends" },
  "war-quiet": { uk: "Ніхто нікого не зачепив", en: "Nobody laid a hand on anybody" },
  "home-language": { uk: "Мова", en: "Language" },
  "home-map": { uk: "Мапа", en: "Map" },
  // The page that draws a whole seed, which is not the picker called "Мапа".
  "home-seed-map": { uk: "Мапа сіда", en: "Seed map" },
  "music": { uk: "Музика", en: "Music" },
  "hand": { uk: "Зброя в руці", en: "Weapon in the" },
  "hand-right": { uk: "правій", en: "right hand" },
  "hand-left": { uk: "лівій", en: "left hand" },
  // A world the game keeps on its own, as against one somebody named.
  "saves-world-kept": { uk: "світ мапи", en: "the map's world" },
  "home-skin": { uk: "Хто ти", en: "Who you are" },

  // ---- playing together ---------------------------------------------------
  "net-together": { uk: "Грати разом", en: "Play together" },
  "net-back": { uk: "← Назад", en: "← Back" },
  "net-join": { uk: "Приєднатись", en: "Join" },
  "net-create": { uk: "Створити", en: "Create" },
  "net-address": { uk: "Адреса сервера", en: "Server address" },
  /*
   * What «Створити» costs, said plainly and before the button is pressed.
   *
   * The world runs on the player's own computer now - not as a choice they
   * make but as how it works - so the honest thing is to say what that
   * means: it uses their machine while they play, and it lasts as long as
   * their tab does. Both of those are things somebody would otherwise find
   * out by losing an evening.
   */
  "net-create-blurb": {
    uk: "Цей світ триматиме твій комп'ютер: поки ти граєш, він " +
      "витрачатиме його ресурси. Інші заходитимуть прямо до тебе.",
    en: "This world will be held by your computer: while you play, it uses " +
      "your machine's resources. Others come straight to you.",
  },
  "net-create-note": {
    uk: "Світ живе, поки відкрита ця вкладка. Закриєш — світу не стане, " +
      "і всі, хто в ньому, вийдуть.",
    en: "The world lives as long as this tab is open. Close it and the " +
      "world is gone, and everybody in it is let go.",
  },

  "net-connect": { uk: "З'єднатись", en: "Connect" },
  "net-checking": { uk: "Стукаю...", en: "Knocking..." },
  "net-found": { uk: "Сервер відповів", en: "The server answered" },
  "net-unreachable": {
    uk: "Не відповідає. Він піднятий? Чи не блокує брандмауер?",
    en: "No answer. Is it running? Is a firewall in the way?",
  },
  "net-late": {
    uk: "Забіг уже почався. Приєднатись можна тільки в першу хвилину — " +
      "зачекай наступного.",
    en: "The race has already started. You can only join in the first " +
      "minute - wait for the next one.",
  },
  "net-mismatch": {
    uk: "Сервер говорить іншою версією протоколу",
    en: "That server speaks a different version of the protocol",
  },
  /*
   * A world's own key is a пароль, not a "слово".
   *
   * It was «слово» everywhere - which is what it is called on the wire and
   * in the environment, and which nobody outside this code has ever called
   * it. Asked for plainly: "поміняй текст зі словом на з паролем". The
   * account's key stays «гасло» so the two cannot be confused; in English
   * they are "password" and "world password" for the same reason.
   */
  "net-word": {
    uk: "Пароль, який дав господар",
    en: "The world password the host gave you",
  },
  "net-wrong-word": {
    uk: "Не той пароль. Спитай у того, хто підняв світ.",
    en: "Wrong world password. Ask whoever is hosting the world.",
  },
  /* The two settings «Сучасна війна» is made with. */
  "net-bots": { uk: "проти ботів", en: "against bots" },
  "net-hours": { uk: "годин гра триває (до 12)", en: "hours the game lasts (up to 12)" },
  "net-hardcore": { uk: "хардкор — скрині з припасами біля прапора", en: "hardcore - chests of supplies by the flag" },
  "net-prep": { uk: "хвилин на підготовку", en: "minutes to prepare" },
  "net-create-word": {
    uk: "Сервер надрукує пароль при запуску — дай його тим, кого пускаєш. " +
      "Свій можна задати так: WORD=калина npm run server",
    en: "The server prints a password when it starts — give it to whoever " +
      "you are letting in. To choose your own: WORD=kalyna npm run server",
  },
  "net-copy": { uk: "Клікни, щоб скопіювати", en: "Click to copy" },
  "net-copied": { uk: "Скопійовано — встав у термінал", en: "Copied — paste it into a terminal" },
  "net-copy-failed": {
    uk: "Не вдалося скопіювати — перепиши вручну",
    en: "Could not copy — type it out",
  },
  "net-name": { uk: "твоє ім'я", en: "your name" },
  "net-password": {
    uk: "гасло (якщо маєш обліковку)",
    en: "password (if you have an account)",
  },
  "net-email": { uk: "пошта — не обов'язково", en: "email - optional" },
  /**
   * Why anybody would give an address to a game about burning a fortress.
   *
   * Said at the field rather than in a page nobody reads: it is the only
   * reason, and somebody deciding whether to type it is deciding right there.
   */
  "net-email-note": {
    uk: "додай пошту, якщо хочеш мати змогу відновити гасло",
    en: "add your email if you want to have ability to reset password",
  },
  "net-register": { uk: "Створити обліковку", en: "Create an account" },
  "net-register-do": { uk: "Створити", en: "Create" },
  "net-forgot": { uk: "Забув гасло", en: "Forgot the password" },
  "net-account-made": {
    uk: "Обліковку створено. Тепер заходь під цим ім'ям.",
    en: "Account made. Now come in under that name.",
  },
  /** Said whoever asked, and deliberately: see `forgot` in the protocol. */
  "net-reset-sent": {
    uk: "Якщо на це ім'я є обліковка з поштою — лист уже летить.",
    en: "If that name has an account with an address, a letter is on its way.",
  },
  "net-reset-title": { uk: "Нове гасло", en: "A new password" },
  "net-reset-do": { uk: "Зберегти гасло", en: "Save the password" },
  "net-reset-done": {
    uk: "Готово. Заходь під новим гаслом.",
    en: "Done. Come in with the new one.",
  },
  "net-reset-stale": {
    uk: "Це посилання вже не працює. Попроси нове.",
    en: "That link no longer works. Ask for another.",
  },
  "net-pass-short": {
    uk: "Гасло закоротке — треба щонайменше вісім знаків",
    en: "That password is too short - eight characters at least",
  },
  "net-pass-long": { uk: "Гасло задовге", en: "That password is too long" },
  "net-name-long": { uk: "Ім'я задовге", en: "That name is too long" },
  "net-name-odd": { uk: "У імені є щось дивне", en: "That name has something odd in it" },
  "net-name-taken": {
    uk: "Це ім'я вже зайняте. Візьми інше.",
    en: "That name is taken. Take another.",
  },
  "net-email-odd": {
    uk: "Це не схоже на пошту",
    en: "That does not look like an address",
  },
  "net-need-name": {
    uk: "Спершу назвися — під цим ім'ям світ тебе й запам'ятає",
    en: "Give a name first - it is the name the world remembers you by",
  },
  "net-taken-name": {
    uk: "Це ім'я вже чиєсь. Візьми інше.",
    en: "That name is somebody else's. Take another.",
  },
  "net-pick-world": {
    uk: "Оберіть світ",
    en: "Pick a world",
  },
  "net-joining": {
    uk: "Заходжу у світ...",
    en: "Going in...",
  },

  // What is wrong with an address somebody typed.
  "net-playing": {
    uk: "Граєш на чужому світі. Нічого не зберігається — світ належить серверу.",
    en: "You are on somebody else's world. Nothing is saved here — the world " +
      "belongs to the server.",
  },
  "net-dropped": {
    uk: "Зв'язок обірвався. Світ далі показується, але його вже ніхто не веде — " +
      "перезавантаж сторінку.",
    en: "The connection dropped. The world is still on screen but nobody is " +
      "running it any more — reload the page.",
  },
  "net-empty": { uk: "Порожня адреса", en: "The address is empty" },
  "net-scheme": {
    uk: "Тільки ws:// або wss://",
    en: "Only ws:// or wss://",
  },
  "net-host": { uk: "Незрозуміла адреса", en: "That address makes no sense" },
  "net-port": { uk: "Порт має бути числом від 1 до 65535",
    en: "The port must be a number from 1 to 65535" },
  /**
   * The button that grows a world, and the line telling you to press it.
   *
   * Two of each, because walking back into a world you were in the middle of
   * is not the same act as starting one, and a button that said "Почати" over
   * a хата you spent an evening on would read as an offer to flatten it.
   */
  "home-begin": { uk: "Почати", en: "Play" },
  "home-resume": { uk: "Продовжити", en: "Continue" },
  "home-save": { uk: "Зберегти", en: "Save" },
  "home-saved": { uk: "Збережено", en: "Saved" },
  "inv-quests": { uk: "Пригоди", en: "Quests" },
  "quests-title": { uk: "Книга пригод", en: "Book of quests" },
  "quests-none": {
    uk: "Поки що нема жодної пригоди",
    en: "No quests yet",
  },
  "quests-claim": { uk: "Забрати", en: "Claim" },
  "quests-done": { uk: "Пройдено", en: "Done" },
  "quests-reward": { uk: "Нагорода", en: "Reward" },
  "quests-full": {
    uk: "Нема місця в торбі — звільни слот",
    en: "No room in the bag - free a slot",
  },
  "nudge-recipes": {
    uk: "Натисни B — нові рецепти",
    en: "Press B - new recipes",
  },
  "book-only": {
    uk: "Тільки те, що можу зробити зараз",
    en: "Only what I can make now",
  },
  "book-none": {
    uk: "Зараз нічого не можеш зробити — пошукай матеріалів",
    en: "Nothing you can make right now - go and find some materials",
  },
  "home-new": { uk: "Почати гру", en: "New game" },
  /*
   * The same button once a world is up: an offer to start this map over.
   *
   * It used to disappear, on the grounds that «Почати гру» over a world you
   * are standing in is an offer to flatten it. It is - and on «Один блок»
   * it is the one thing somebody who has fallen off wants, and there was
   * nothing to press. Named for what it does instead of hidden.
   */
  "home-restart": { uk: "Почати спочатку", en: "Start over" },
  "home-adventure": { uk: "Почати пригоду", en: "Start adventure" },
  "home-solo": { uk: "Гра наодинці", en: "Play alone" },
  "round-gather": { uk: "Збір — стій", en: "Gathering - hold" },
  "round-over": { uk: "Раунд закінчено", en: "Round over" },
  "round-won": { uk: "Король гори", en: "King of the hill" },
  "round-nobody": { uk: "Ніхто не дійшов", en: "Nobody made it" },
  "round-again": { uk: "Зіграти ще раз", en: "Play again" },
  "round-soon": {
    uk: "На головний екран за",
    en: "Back to the menu in",
  },
  "round-leave": { uk: "Вийти з гри", en: "Leave" },
  "round-climb": { uk: "До верху", en: "To the top" },
  "net-refresh": { uk: "Оновити список", en: "Refresh the list" },
  "net-start": { uk: "Почати гру", en: "Start the game" },
  "net-locked": { uk: "з паролем", en: "needs a password" },
  "net-need-word": {
    uk: "Цей світ із паролем — введи його",
    en: "This world has a password - type it",
  },
  "net-made": { uk: "Світ готовий. Заходимо…", en: "The world is ready. Going in…" },
  /*
   * Why a world could not be held. Keys the screen translates, never
   * sentences from the server - it does not decide what language anybody
   * reads.
   */
  "net-login-needed": {
    uk: "Увійди в обліковку, щоб створити гру",
    en: "Sign in to your account to create a game",
  },
  "net-login": { uk: "Увійти", en: "Sign in" },
  "net-join-login-needed": {
    uk: "Увійди в обліковку, щоб доєднатись до гри",
    en: "Sign in to your account to join a game",
  },
  "net-join-world": { uk: "Доєднатись", en: "Join" },
  "net-gone": {
    uk: "Цього світу вже немає. Онови список",
    en: "That world is no longer there. Refresh the list",
  },
  "net-full": { uk: "У цьому світі вже повно", en: "That world is full" },
  "no-flying-here": { uk: "На цій мапі літати не можна", en: "Nobody flies on this map" },
  "net-shared-time": {
    uk: "Час у спільній грі один для всіх — його не зупинити й не пришвидшити",
    en: "Time in a shared game is the same for everybody - it cannot be paused or sped up",
  },
  "net-start-again": { uk: "Почати заново", en: "Start again" },
  "net-start-again-ask": {
    uk: "Стерти цей світ і почати новий? Усе, що в ньому збудовано, зникне назавжди.",
    en: "Delete this world and start a new one? Everything built in it will be gone for good.",
  },
  "net-started-again": {
    uk: "Старий світ стерто. Обери мапу й тисни «Почати гру»",
    en: "The old world is deleted. Choose a map and press «Start the game»",
  },
  "net-host-silent": {
    uk: "Господар не відповів. Його вкладка відкрита?",
    en: "The host did not answer. Is their tab still open?",
  },
  "net-no-path": {
    uk: "Господар відповів, але зв'язок між комп'ютерами не встановився",
    en: "The host answered, but the two computers could not reach each other",
  },
  "net-login-title": { uk: "Вхід", en: "Sign in" },
  "net-signed-as": { uk: "Ти увійшов як", en: "Signed in as" },
  "net-sign-out": { uk: "Вийти", en: "Sign out" },
  "net-cancel": { uk: "Скасувати", en: "Cancel" },
  "net-wrong-login": {
    uk: "Не те ім'я або не те гасло",
    en: "Wrong name or wrong password",
  },
  "net-session-stale": {
    uk: "Сервер тебе вже не пам'ятає. Увійди ще раз",
    en: "The server no longer knows you. Sign in again",
  },
  "net-sign-in": {
    uk: "Щоб тримати світ, треба обліковка: ім'я з гаслом",
    en: "Holding a world needs an account: a name with a password",
  },
  "net-your-worlds-full": {
    uk: "Ти вже тримаєш світ. Закрий ту вкладку, щоб підняти інший",
    en: "You are already holding a world. Close that tab to hold another",
  },
  "net-server-full": {
    uk: "Зараз піднято забагато світів. Спробуй трохи пізніше",
    en: "Too many worlds are up right now. Try again shortly",
  },
  "net-no-hosting": {
    uk: "Цей сервер поки не зводить гравців один з одним",
    en: "This server is not introducing players to each other yet",
  },
  "net-no-such-map": { uk: "Такої мапи немає", en: "No such map" },
  /*
   * A word on the row for a world held in somebody's browser.
   *
   * Worth saying on the row rather than as a surprise afterwards: that
   * world goes when their tab does, and how well it runs is how good their
   * machine and their line are - neither of which is true of the worlds
   * this server holds itself.
   */
  "net-guest-world": { uk: "на комп'ютері гравця", en: "on a player's computer" },
  /*
   * Continuing a world this browser is already holding.
   *
   * Closing the tab used to be closing the world for good. Now it is
   * written down here and comes back - the same country, the same holes
   * dug in it, the same bags, and the same password on the door, so that
   * the people who were in it can come back too.
   */
  "net-continue": { uk: "Продовжити", en: "Continue" },
  "net-continue-blurb": {
    uk: "Твій світ на місці. Пароль у нього той самий, тож усі, хто грав, " +
      "можуть повернутись. Це:",
    en: "Your world is still here. Its password is the same one, so everybody " +
      "who was playing can come back. It is:",
  },
  "home-solo-head": { uk: "Гра наодинці", en: "Playing alone" },
  "home-back": { uk: "Назад", en: "Back" },
  "home-adventure-over": {
    uk: "У тебе вже є пригода. Почати нову? Стара лишиться тільки в резерві.",
    en: "You already have an adventure. Start a new one? The old one will only be in the backup.",
  },
  "home-pick-first": {
    uk: "вибери мапу й тисни «Почати гру»",
    en: "choose a map, then press New game",
  },
  /**
   * Starting a new world where one already stands.
   *
   * Asked rather than done, because the live slot is per map and a fresh
   * world autosaves straight over it. There is a backup slot behind it, but
   * "your evening is in the бекап" is not something anybody should have to
   * be told.
   */
  "home-new-over": {
    uk: "На цій мапі вже є твій світ. Почати новий? Старий лишиться тільки в резерві.",
    en: "You already have a world on this map. Start a new one? The old one will only be in the backup.",
  },
  "home-resume-note": {
    uk: "твій світ чекає — тисни «Продовжити»",
    en: "your world is waiting - press Continue",
  },


  // ---- the save manager --------------------------------------------------
  /**
   * What the save panel says when the world is not yours to save.
   *
   * Not "saving is disabled" - nothing is disabled, there is simply no such
   * thing here. The server keeps this world for everybody at once, which is
   * more than a personal file ever did.
   */
  "saves-on-server": {
    uk: "Це світ сервера. Він зберігає його сам, для всіх одразу — " +
      "власних збережень тут немає.",
    en: "This is the server's world. It keeps it itself, for everybody at " +
      "once - there are no personal saves here.",
  },
  "saves-live": { uk: "Поточний світ", en: "Current world" },
  "saves-live-none": { uk: "ще не збережений", en: "not saved yet" },
  "saves-backup": { uk: "Резерв", en: "Backup" },
  "saves-backup-none": {
    uk: "порожній — повертати нема чого",
    en: "empty — nothing to go back to",
  },
  "saves-name-placeholder": { uk: "назва збереження", en: "name this save" },
  "saves-save": { uk: "Зберегти", en: "Save" },
  "saves-play": { uk: "Грати", en: "Play" },
  "saves-rename": { uk: "Назва", en: "Rename" },
  "saves-delete": { uk: "Видалити", en: "Delete" },
  "saves-none": {
    uk: "Збережень ще немає. Назви це і натисни «Зберегти»",
    en: "No saves yet. Give this one a name and press Save",
  },
  "saves-unreadable": {
    uk: "Не вдалося прочитати збереження",
    en: "Could not read the saved games",
  },
  "saves-files": { uk: "Файли й резерв", en: "Files and backup" },
  "saves-export": { uk: "Експорт у файл", en: "Export to a file" },
  "saves-import": { uk: "Імпорт з файлу", en: "Import from a file" },
  "saves-restore": { uk: "Повернути попередній", en: "Bring back the previous" },
  "saves-fresh": { uk: "Почати заново", en: "Start again" },
  "saves-write-failed": { uk: "Не вдалося зберегти", en: "Could not save" },
  "saves-rename-failed": { uk: "Не вдалося перейменувати", en: "Could not rename" },
  "saves-delete-failed": { uk: "Не вдалося видалити", en: "Could not delete" },

  // Questions asked before something irreversible.
  "saves-rename-ask": { uk: "Нова назва для", en: "New name for" },
  "saves-load-ask": { uk: "Завантажити", en: "Load" },
  "saves-load-note": {
    uk: "Світ, у якому ти зараз, піде в резервний слот — його можна буде " +
      "повернути кнопкою «Повернути попередній».",
    en: "The world you are in now goes to the backup slot — you can bring it " +
      "back with “Bring back the previous”.",
  },
  "saves-delete-ask": { uk: "Видалити", en: "Delete" },
  "saves-delete-note": { uk: "Це назавжди.", en: "This cannot be undone." },

  // ---- names a save cannot have ------------------------------------------
  "slot-empty": { uk: "Назва не може бути порожня", en: "A name cannot be empty" },
  "slot-long": { uk: "Задовга назва — не більше", en: "Too long — at most" },
  "slot-long-tail": { uk: "символів", en: "characters" },
  "slot-odd": {
    uk: "Назва містить дивні символи",
    en: "That name has strange characters in it",
  },
  "slot-taken": { uk: "Така назва вже є", en: "There is already one called that" },

  // ---- the bag -----------------------------------------------------------
  "inv-title": { uk: "Речі", en: "Bag" },
  "inv-book": { uk: "Рецепти", en: "Recipes" },
  "inv-store": { uk: "Комора", en: "Storeroom" },
  "inv-store-title": {
    uk: "Комора — бери що завгодно",
    en: "The storeroom — take what you like",
  },
  "inv-close": { uk: "Закрити", en: "Close" },
  "inv-chest": { uk: "Скриня", en: "Chest" },
  "inv-stove": { uk: "Піч", en: "Stove" },
  "inv-armour": { uk: "Броня", en: "Armour" },
  "inv-help": {
    uk: "ЛКМ взяти / покласти · ПКМ половину / по одному · " +
      "Ctrl+ЛКМ перекинути стос · B рецепти · E або Esc закрити",
    en: "Left-click take / put · right-click half / one · " +
      "Ctrl+left-click send the stack · B recipes · E or Esc to close",
  },

  // ---- shelves in the storeroom ------------------------------------------
  "shelf-terrain": { uk: "земля й камінь", en: "earth and stone" },
  "shelf-building": { uk: "будівництво", en: "building" },
  "shelf-plants": { uk: "рослини", en: "plants" },
  "shelf-creatures": { uk: "живність", en: "creatures" },
  "shelf-tools": { uk: "знаряддя", en: "tools" },
  "shelf-armour": { uk: "броня", en: "armour" },
  "shelf-food": { uk: "їжа", en: "food" },
  "shelf-other": { uk: "решта", en: "the rest" },

  // ---- the crafting square -----------------------------------------------
  "craft-needs": { uk: "потрібна", en: "needs a" },
  "craft-one-each": { uk: "по одному в клітинку", en: "one in each square" },
  "craft-shape": { uk: "склади за формою", en: "lay it out in the shape" },

  // ---- the head-up display ------------------------------------------------
  "hud-health": { uk: "сила", en: "strength" },
  "hud-armour": { uk: "броня", en: "armour" },
  "cam-back": { uk: "3rd ззаду", en: "3rd behind" },
  "cam-front": { uk: "3rd спереду", en: "3rd in front" },

  // ---- choosing a козак ---------------------------------------------------
  "skins-title": { uk: "Козаки", en: "Cossacks" },
  "skins-hint": {
    uk: "K закрити · C вид від третьої особи",
    en: "K to close · C for the third-person view",
  },

  // ---- whole-world questions ----------------------------------------------
  "ask-fresh": {
    uk: "Почати заново?\n\nПоточний світ буде замінено новим. " +
      "Його копію збережено в резервному слоті.",
    en: "Start again?\n\nThis world will be replaced by a new one. " +
      "A copy of it is kept in the backup slot.",
  },
  "no-backup": { uk: "резервного світу немає", en: "there is no backup world" },
  "ask-restore": {
    uk: "Повернути попередній світ?",
    en: "Bring back the previous world?",
  },
  "ask-restore-moved": {
    uk: "Його виростила інша версія генерації. Все, що ти будував і копав, " +
      "повернеться як було — незаймана земля навколо виросте по-новому, " +
      "і на межі може бути видно шов.",
    en: "It was grown by a different version of the generator. Everything you " +
      "built and dug comes back as it was — the untouched land around it " +
      "grows anew, and there may be a visible seam at the join.",
  },
  "ask-restore-note": {
    uk: "Поточний світ піде в резервний слот — цю дію можна повернути тим " +
      "самим способом.",
    en: "The current world goes to the backup slot — this can be undone the " +
      "same way.",
  },
  "ask-import": {
    uk: "Завантажити збереження?",
    en: "Load this saved game?",
  },
  "ask-import-note": {
    uk: "Поточний світ буде замінено. Його копію збережено в резервному слоті.",
    en: "The current world will be replaced. A copy is kept in the backup slot.",
  },
  "save-unreadable": {
    uk: "Збереження не прочиталося. Автозбереження вимкнено, щоб його не " +
      "затерти — копія в резервному слоті, тисни «Збереження» → «Повернути " +
      "попередній»",
    en: "The saved game could not be read. Autosaving is off so it is not " +
      "written over — a copy is in the backup slot; open “Saved games” and " +
      "press “Bring back the previous”",
  },
  "not-a-save": { uk: "не збереження", en: "not a saved game" },
  "unreadable": { uk: "не читається", en: "cannot be read" },
  "not-found": { uk: "не знайдено", en: "not found" },
  "saved-as": { uk: "збережено як", en: "saved as" },
} as const satisfies Record<string, Phrase>;

export type UiKey = keyof typeof UI;

/** One phrase of interface text, in the language in force. */
export function ui(key: UiKey, lang: Lang): string {
  return pick(UI[key], lang);
}

/**
 * The list of keys, as data rather than as markup.
 *
 * It used to be twenty lines of hand-written HTML with the Ukrainian and a
 * rough English jammed onto the same line ("рухатись / move"). As a table it
 * is one row per key, both languages required, and a test can walk it.
 */
export const CONTROLS: readonly { readonly key: Phrase; readonly what: Phrase }[] = [
  { key: { uk: "W A S D", en: "W A S D" }, what: { uk: "рухатись", en: "move" } },
  { key: { uk: "Space", en: "Space" }, what: { uk: "стрибок", en: "jump" } },
  { key: { uk: "Shift", en: "Shift" }, what: {
    uk: "навприсядки — з блока не впасти",
    en: "crouch - you cannot walk off a block" } },
  { key: { uk: "Z", en: "Z" }, what: { uk: "бігти", en: "run" } },
  { key: { uk: "Mouse", en: "Mouse" }, what: { uk: "озирнутись", en: "look around" } },
  { key: { uk: "ЛКМ", en: "LMB" }, what: { uk: "зламати", en: "break" } },
  { key: { uk: "ПКМ", en: "RMB" }, what: {
    uk: "поставити · відчинити двері", en: "place · open a door" } },
  { key: { uk: "1-9", en: "1-9" }, what: { uk: "вибрати блок", en: "pick a block" } },
  { key: { uk: "Q", en: "Q" }, what: { uk: "викинути річ", en: "drop what you hold" } },
  { key: { uk: "ПКМ", en: "RMB" }, what: {
    uk: "по печі — топити, по скрині — відчинити",
    en: "on a stove to light it, on a chest to open it" } },
  { key: { uk: "Wheel", en: "Wheel" }, what: { uk: "гортати пояс", en: "scroll the belt" } },
  { key: { uk: "E", en: "E" }, what: { uk: "речі", en: "your bag" } },
  { key: { uk: "K", en: "K" }, what: { uk: "козак", en: "your character" } },
  { key: { uk: "B", en: "B" }, what: { uk: "рецепти (в інвентарі)", en: "recipes (in the bag)" } },
  { key: { uk: "C", en: "C" }, what: {
    uk: "вид: 1-ша / ззаду / спереду",
    en: "view: first / behind / in front" } },
  { key: { uk: "F", en: "F" }, what: { uk: "політ", en: "fly mode" } },
  { key: { uk: "G", en: "G" },
    what: { uk: "повернути дрон", en: "bring the drone down" } },
  { key: { uk: "ЛКМ", en: "LMB" },
    what: { uk: "з дрона — скинути снаряд", en: "from the drone: release a bolt" } },
  { key: { uk: "T", en: "T" }, what: { uk: "швидкість часу", en: "speed of time" } },
  { key: { uk: "Ctrl+S", en: "Ctrl+S" }, what: { uk: "зберегти", en: "save" } },
  { key: { uk: "Ctrl+E", en: "Ctrl+E" }, what: { uk: "експорт у файл", en: "export to a file" } },
  { key: { uk: "Ctrl+O", en: "Ctrl+O" }, what: { uk: "імпорт з файлу", en: "import from a file" } },
  { key: { uk: "Esc", en: "Esc" }, what: { uk: "відпустити курсор", en: "release the cursor" } },
];
